'use client';

import Image from 'next/image';
import { useEffect, useState, useTransition } from 'react';
import RedeCredenciadaList from './_components/rede-credenciada-list';
import { Estabelecimento, TipoFiltro } from '@/types/rede-credenciada';
import { searchByCepAction, searchByCoordsAction } from './actions';
import { TextInputWithButton } from './_components/textwithbutton';
import { formatCEP } from '@/lib/formatters';

const type_filters = [
  { value: 1, label: 'Estacionamentos' },
  { value: 2, label: 'Postos de Combustível' },
  { value: 4, label: 'Pedágios' },
  { value: 5, label: 'Drive-thru' },
  { value: 6, label: 'Lava-rápido' },
];

export default function RedeCredenciada() {
  const [selected, setSelected] = useState<TipoFiltro | null>(null);
  const [cep, setCep] = useState('');
  const [estabelecimentos, setEstabelecimentos] = useState<Estabelecimento[]>(
    []
  );
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  const canSearch = selected !== null;

  const isLoading = isPending || isLocating;

  useEffect(() => {
    if (selected != null) {
      if (cep != '') {
        onSubmitCep();
      } else {
        onUseLocation();
      }
    }
  }, [selected]);

  const onSubmitCep = () => {
    if (!canSearch) return setError('Selecione um filtro.');
    setError(null);

    startTransition(async () => {
      try {
        const res = await searchByCepAction(cep, selected);
        setEstabelecimentos(res);
      } catch {
        setError('Erro ao buscar por CEP.');
      }
    });
  };

  const onUseLocation = () => {
    if (!canSearch) return setError('Selecione um filtro.');
    setError(null);

    setIsLocating(true);

    // Geolocalização do browser (não é HTTP) — depois mandamos pro servidor
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        startTransition(async () => {
          try {
            const res = await searchByCoordsAction(c, selected);
            setEstabelecimentos(res);
          } catch {
            setError('Erro ao buscar pela localização.');
          } finally {
            setIsLocating(false);
          }
        });
      },
      () => {
        setError('Não foi possível obter sua localização.');
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const renderSearchControls = (isMobile: boolean) => (
    <div
      className={`flex flex-col gap-3 ${isMobile ? 'lg:hidden w-full' : 'hidden lg:flex'}`}
    >
      {/* Input de CEP com ícone */}
      <TextInputWithButton
        value={cep}
        onChange={setCep}
        onSubmit={onSubmitCep}
        maxLength={9}
        placeholder="Digite seu CEP!"
        formatter={formatCEP}
        disabled={!canSearch}
        isPending={isLoading}
        disabledTitle="Digite um CEP válido"
        enabledTitle="Buscar por CEP"
      />

      {/* Botão de localização */}
      <button
        onClick={onUseLocation}
        disabled={!canSearch || isLoading}
        className="flex items-center text-sm justify-center gap-2 bg-bluecaixa text-white rounded-lg py-4 px-4 font-medium disabled:opacity-60"
        title={!canSearch ? 'Selecione um filtro' : 'Usar localização atual'}
      >
        <Image
          src="/icons/map-pin-white.png"
          alt="Localização"
          width={20}
          height={20}
        />
        Usar localização atual
      </button>

      {error && <p className="text-red-600 text-sm">{error}</p>}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl my-12 lg:my-24 flex justify-between px-4 lg:px-12 flex-col lg:flex-row border-2 rounded-3xl h-auto lg:h-[600px] py-8 lg:py-0">
      <div className="w-full lg:w-60 flex flex-col justify-center gap-5">
        <h1 className="text-3xl font-extrabold leading-snug">
          <p className="text-bluecaixa">Encontre o estabelecimento </p>
          <p className="text-orangecaixa">ideal para você</p>
        </h1>

        <p className="text-base text-[#464646]">
          Chega de filas! Use Sem Parar em todos os lugares, na estrada ou na
          cidade.
        </p>

        {/* CEP ou Geolocalização (Desktop only) */}
        {renderSearchControls(false)}
      </div>

      <div className="flex-1 flex flex-col justify-center gap-6 pl-0 lg:pl-8 mt-8 lg:mt-0">
        {/* Filtros (exatamente 1) */}
        <div className="flex flex-row flex-wrap gap-2">
          {type_filters.map((filtro) => (
            <button
              key={filtro.value}
              onClick={() => setSelected(filtro.value as TipoFiltro)}
              className={`px-4 py-2 border-2 rounded-lg transition-colors duration-200
                ${
                  selected === filtro.value
                    ? 'bg-bluecaixa text-white border-bluecaixa'
                    : 'bg-transparent text-[#464646] border-[#DCDCDC] hover:bg-[#f8f8f8]'
                }`}
            >
              {filtro.label}
            </button>
          ))}
        </div>

        {/* CEP ou Geolocalização (Mobile only - entre filtros e lista) */}
        {renderSearchControls(true)}

        {/* Lista / Loading */}
        <RedeCredenciadaList
          isLoading={isLoading}
          estabelecimentos={estabelecimentos}
        />
      </div>
    </div>
  );
}
